/**
 * Proposal Provenance & Confidence — pure schema + evidence builder/validator.
 *
 * Turns "human-approved" into informed consent: every agent proposal can
 * carry a machine-checkable WHY — click-verifiable evidence citations plus a
 * scored confidence with its basis — and a deterministic digest ready to be
 * sealed into the Decision Ledger.
 *
 * Deliberately pure: no store, no firebase, no auth. The only import is the
 * shipped FNV-1a digest from the ledger chain verifier so a provenance
 * digest uses the SAME hashing approach the tamper-evident chain seals with.
 */

import { fnvHash } from '../ledger/verifyChain';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** A click-verifiable citation backing a proposal. */
export type EvidenceRef = {
  kind: 'account' | 'ticket' | 'deliverable' | 'ledger' | 'email' | 'meetNote' | 'task';
  id: string;
  /** Human-readable label shown on the confirm card. */
  label: string;
  /** In-app route the citation resolves to (see {@link deepLinkFor}). */
  deepLink: string;
};

/** How sure the agent is, and why. */
export type Confidence = {
  /** 0..1 — clamped by {@link buildProvenance}. */
  score: number;
  /** Non-empty plain-English basis for the score. */
  basis: string;
};

/** The full WHY attached to a proposal. */
export type ProposalProvenance = {
  evidence: EvidenceRef[];
  confidence: Confidence;
};

const EVIDENCE_KINDS: ReadonlyArray<EvidenceRef['kind']> = [
  'account',
  'ticket',
  'deliverable',
  'ledger',
  'email',
  'meetNote',
  'task',
];

/** Route segment per evidence kind. */
const ROUTE_BY_KIND: Record<EvidenceRef['kind'], string> = {
  account: 'accounts',
  ticket: 'tickets',
  deliverable: 'deliverables',
  ledger: 'ledger',
  email: 'emails',
  meetNote: 'meet-notes',
  task: 'tasks',
};

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/** Canonical in-app route for a citation, e.g. `/accounts/:id`. */
export function deepLinkFor(kind: EvidenceRef['kind'], id: string): string {
  return `/${ROUTE_BY_KIND[kind]}/${id}`;
}

/** Clamp a number into [0, 1]; non-finite scores collapse to 0. */
function clampScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  return Math.min(1, Math.max(0, score));
}

/**
 * Normalize a provenance payload:
 *   • dedupes evidence by `kind + id` (first occurrence wins);
 *   • clamps the confidence score into [0, 1];
 *   • trims the basis.
 * Pure — the input object is never mutated.
 */
export function buildProvenance(input: {
  evidence: EvidenceRef[];
  confidence: Confidence;
}): ProposalProvenance {
  const seen = new Set<string>();
  const evidence: EvidenceRef[] = [];
  for (const ref of input.evidence) {
    const key = `${ref.kind}\u0000${ref.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    evidence.push({ ...ref });
  }
  return {
    evidence,
    confidence: {
      score: clampScore(input.confidence.score),
      basis: input.confidence.basis.trim(),
    },
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Machine-check a provenance payload before it is attached or sealed.
 * Returns every problem found (not just the first) so a confirm card can
 * show exactly what is missing from the WHY.
 */
export function validateProvenance(p: unknown): { ok: boolean; errors: string[] } {
  const errors: string[] = [];

  if (typeof p !== 'object' || p === null) {
    return { ok: false, errors: ['provenance must be an object'] };
  }
  const o = p as Record<string, unknown>;

  // Evidence — at least one, each with kind/id/deepLink.
  if (!Array.isArray(o.evidence) || o.evidence.length === 0) {
    errors.push('evidence must contain at least one reference');
  } else {
    o.evidence.forEach((ref, i) => {
      if (typeof ref !== 'object' || ref === null) {
        errors.push(`evidence[${i}] must be an object`);
        return;
      }
      const r = ref as Record<string, unknown>;
      if (
        typeof r.kind !== 'string' ||
        !EVIDENCE_KINDS.includes(r.kind as EvidenceRef['kind'])
      ) {
        errors.push(`evidence[${i}] has invalid kind`);
      }
      if (typeof r.id !== 'string' || r.id.trim() === '') {
        errors.push(`evidence[${i}] is missing id`);
      }
      if (typeof r.deepLink !== 'string' || r.deepLink.trim() === '') {
        errors.push(`evidence[${i}] is missing deepLink`);
      }
    });
  }

  // Confidence — score in [0, 1], non-empty basis.
  const confidence = o.confidence as Record<string, unknown> | undefined;
  if (typeof confidence !== 'object' || confidence === null) {
    errors.push('confidence must be an object');
  } else {
    const score = confidence.score;
    if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 1) {
      errors.push('confidence score must be a number in [0, 1]');
    }
    if (typeof confidence.basis !== 'string' || confidence.basis.trim() === '') {
      errors.push('confidence basis must be a non-empty string');
    }
  }

  return { ok: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Digest
// ---------------------------------------------------------------------------

/**
 * Stable JSON canonicalization → deterministic string hash.
 *
 * Evidence is sorted by kind+id and each ref is serialized with a fixed
 * field order, so the SAME provenance yields the SAME digest regardless of
 * the order evidence was gathered in. The digest reuses the shipped FNV-1a
 * seal from the ledger chain verifier ('fnv1a:' prefix), ready to seal.
 */
export function provenanceDigest(p: ProposalProvenance): string {
  const evidence = [...p.evidence]
    .sort((a, b) => {
      const ka = `${a.kind}\u0000${a.id}`;
      const kb = `${b.kind}\u0000${b.id}`;
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    })
    .map((r) => [r.kind, r.id, r.label, r.deepLink]);
  const canonical = JSON.stringify([evidence, p.confidence.score, p.confidence.basis]);
  return fnvHash('', canonical);
}

// ---------------------------------------------------------------------------
// Attachment
// ---------------------------------------------------------------------------

/**
 * Attach provenance to a proposal action WITHOUT mutating the original —
 * returns a new object carrying a `provenance` field.
 */
export function attachProvenance<T extends object>(
  action: T,
  p: ProposalProvenance,
): T & { provenance: ProposalProvenance } {
  return { ...action, provenance: p };
}
